import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, btnPrimary, btnSecondary } from '../styles/theme.js'
import { scenarioPacks as api, stylePresets as stylePresetsApi } from '../lib/api.js'

// --- Styles ---

const writerCard = css`
  ${card};
  max-width: 800px;
  margin: 0 auto ${space.xl};
`

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.lg};
`

const sectionLabel = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.semibold};
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${space.sm};
`

const chipWrap = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: ${space.lg};
`

const chip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: ${font.size.sm};
  cursor: pointer;
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  user-select: none;
  transition: background 0.1s, border-color 0.1s;
  &:hover { border-color: ${colors.brand}; }
`

const chipActive = css`
  ${chip};
  background: ${colors.brand};
  border-color: ${colors.brand};
  color: white;
  &:hover { background: ${colors.brandDark}; border-color: ${colors.brandDark}; }
`

const detailChip = css`
  ${chip};
  position: relative;
`

const detailChipActive = css`
  ${detailChip};
  background: ${colors.brandLight};
  border-color: ${colors.brand};
  color: ${colors.text};
`

const styleDisplay = css`
  padding: ${space.sm} ${space.md};
  border-radius: 8px;
  border: 1px solid ${colors.border};
  background: ${colors.bgSidebar};
  margin-bottom: ${space.lg};
`

const styleDisplayClickable = css`
  ${styleDisplay};
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: ${colors.brand}; }
`

const dropdown = css`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  min-width: 160px;
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  box-shadow: 0 4px 12px ${colors.shadow};
  padding: ${space.xs} 0;
  max-height: 240px;
  overflow-y: auto;
`

const dropdownItem = css`
  padding: ${space.xs} ${space.md};
  font-size: ${font.size.sm};
  cursor: pointer;
  &:hover { background: ${colors.bgSidebar}; }
`

const dropdownItemActive = css`
  ${dropdownItem};
  background: ${colors.brandLight};
  font-weight: ${font.weight.medium};
`

const textArea = css`
  ${input};
  min-height: 240px;
  resize: vertical;
  font-size: ${font.size.base};
  line-height: 1.6;
  margin-bottom: ${space.lg};
`

const buttonBar = css`
  display: flex;
  gap: ${space.md};
  justify-content: center;
  flex-wrap: wrap;
`

const bigBtn = css`
  display: inline-flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.md} ${space.lg};
  border-radius: 8px;
  font-size: ${font.size.base};
  font-weight: ${font.weight.semibold};
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
`

const bigBtnPrimary = css`
  ${bigBtn};
  background: ${colors.brand};
  color: white;
  &:hover { background: ${colors.brandDark}; }
  &:disabled { opacity: 0.5; cursor: default; }
`

const bigBtnSecondary = css`
  ${bigBtn};
  background: ${colors.bgCard};
  color: ${colors.text};
  border: 2px solid ${colors.border};
  &:hover { border-color: ${colors.brand}; background: ${colors.bgSidebar}; }
  &:disabled { opacity: 0.5; cursor: default; }
`

const savedToast = css`
  text-align: center;
  color: ${colors.green};
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  margin-top: ${space.sm};
  animation: fadeOut 2s forwards;
  @keyframes fadeOut {
    0% { opacity: 1; }
    70% { opacity: 1; }
    100% { opacity: 0; }
  }
`

const scenarioContext = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  background: ${colors.bgSidebar};
  padding: ${space.sm} ${space.md};
  border-radius: 6px;
  margin-bottom: ${space.lg};
  white-space: pre-wrap;
`

const emptyState = css`
  text-align: center;
  padding: ${space.xl};
  color: ${colors.textMuted};
`

const counterBadge = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  text-align: center;
  margin-top: ${space.xs};
`

// --- Helpers ---

function shuffleDetailValues(details) {
  const values = {}
  for (const d of details) {
    if (d.type === 'enum' && d.options && d.options.length > 0) {
      const opt = d.options[Math.floor(Math.random() * d.options.length)]
      values[d.id] = typeof opt === 'string' ? opt : opt.id
    } else if (d.type === 'boolean') {
      values[d.id] = Math.random() < 0.5 ? 'true' : 'false'
    } else if (d.type === 'range') {
      const min = d.range_min || 0
      const max = d.range_max || 100
      values[d.id] = String(min + Math.floor(Math.random() * (max - min + 1)))
    }
  }
  return values
}

function getValueLabel(detail, valueId) {
  if (valueId === '_unknown') return 'Unknown'
  if (detail.type === 'boolean') return valueId === 'true' ? 'Yes' : 'No'
  if (detail.type === 'range') return valueId
  if (detail.options) {
    const opt = detail.options.find(o => (typeof o === 'string' ? o : o.id) === valueId)
    if (opt) return typeof opt === 'string' ? opt : (opt.label || opt.id)
  }
  return valueId
}

function getDetailOptions(detail) {
  const opts = [{ id: '_unknown', label: 'Unknown' }]
  if (detail.type === 'enum' && detail.options) {
    for (const o of detail.options) {
      opts.push(typeof o === 'string' ? { id: o, label: o } : { id: o.id, label: o.label || o.id })
    }
  } else if (detail.type === 'boolean') {
    opts.push({ id: 'true', label: 'Yes' }, { id: 'false', label: 'No' })
  } else if (detail.type === 'range') {
    const min = detail.range_min || 0
    const max = detail.range_max || 100
    const step = Math.max(1, Math.floor((max - min) / 10))
    for (let v = min; v <= max; v += step) {
      opts.push({ id: String(v), label: String(v) })
    }
    if ((max - min) % step !== 0) opts.push({ id: String(max), label: String(max) })
  }
  return opts
}

// --- Component ---

export function ScenarioWriterView() {
  let projectId, packId, scenarioId, generationId
  let isRemix = false
  let pack = null
  let scenario = null
  let loading = true
  let error = null
  let saving = false
  let savedCount = 0
  let showSavedToast = false

  // Chip state
  let detailValues = {}        // { detailId: valueId }
  let selectedStyleId = null
  let activeNoiseDetails = new Set()  // irrelevant detail IDs toggled on
  let noiseDetailValues = {}          // { detailId: valueId } for active irrelevant details
  let activeSnippets = new Set()      // noise snippet IDs toggled on
  let openDropdown = null             // detail ID of open dropdown
  let showExamples = false

  // Data
  let relevantDetails = []
  let irrelevantDetails = []
  let noiseSnippets = []
  let stylePresets = []
  let remixGeneration = null

  // Content
  let content = ''

  function buildConfigSnapshot() {
    const omitted = Object.entries(detailValues)
      .filter(([, v]) => v === '_unknown')
      .map(([id]) => id)
    // Merge irrelevant detail values into the overall detailValues
    const allDetailValues = { ...detailValues }
    for (const did of activeNoiseDetails) {
      if (noiseDetailValues[did]) allDetailValues[did] = noiseDetailValues[did]
    }
    return {
      detailValues: allDetailValues,
      omittedDetails: omitted,
      irrelevantDetailsUsed: [...activeNoiseDetails],
      noiseSnippetsUsed: [...activeSnippets],
      stylePresetId: selectedStyleId,
    }
  }

  function cycleStyle() {
    if (stylePresets.length <= 1) return
    const currentIdx = stylePresets.findIndex(p => p.id === selectedStyleId)
    const nextIdx = (currentIdx + 1) % stylePresets.length
    selectedStyleId = stylePresets[nextIdx].id
    showExamples = false
  }

  function shuffle() {
    detailValues = shuffleDetailValues(relevantDetails)
    // Randomize style
    selectedStyleId = stylePresets.length > 0
      ? stylePresets[Math.floor(Math.random() * stylePresets.length)].id
      : null
    // Randomize irrelevant detail selections and values
    const newNoise = new Set()
    noiseDetailValues = {}
    for (const irr of irrelevantDetails) {
      const did = irr.detail_id || irr
      if (Math.random() < 0.4) {
        newNoise.add(did)
        const d = (pack.details || []).find(dd => dd.id === did)
        if (d) {
          const vals = shuffleDetailValues([d])
          if (vals[d.id]) noiseDetailValues[d.id] = vals[d.id]
        }
      }
    }
    activeNoiseDetails = newNoise
    // Randomize snippet selections
    const newSnippets = new Set()
    for (const s of noiseSnippets) {
      if (Math.random() < 0.3) newSnippets.add(s.id)
    }
    activeSnippets = newSnippets
    content = ''
  }

  async function save() {
    if (!content.trim()) return
    saving = true
    m.redraw()
    try {
      if (isRemix) {
        // handled separately by saveRemix / saveAsNew
        return
      }
      await api.writeGeneration(projectId, packId, scenarioId, {
        content: content.trim(),
        configSnapshot: buildConfigSnapshot(),
      })
      savedCount++
      showSavedToast = true
      setTimeout(() => { showSavedToast = false; m.redraw() }, 2000)
    } catch (e) {
      error = e.message
    } finally {
      saving = false
      m.redraw()
    }
  }

  async function saveAndShuffle() {
    await save()
    if (!error) shuffle()
    m.redraw()
  }

  async function saveAndRepeat() {
    await save()
    if (!error) content = ''
    m.redraw()
  }

  async function saveRemix() {
    if (!content.trim()) return
    saving = true
    m.redraw()
    try {
      await api.updateGeneration(projectId, packId, generationId, { content: content.trim() })
      navigateBack()
    } catch (e) {
      error = e.message
      saving = false
      m.redraw()
    }
  }

  async function saveAsNew() {
    if (!content.trim()) return
    saving = true
    m.redraw()
    try {
      await api.forkGeneration(projectId, packId, generationId, { content: content.trim() })
      navigateBack()
    } catch (e) {
      error = e.message
      saving = false
      m.redraw()
    }
  }

  function navigateBack() {
    m.route.set(`/projects/${projectId}/scenarios/${packId}/${scenarioId}/generate`)
  }

  async function load() {
    loading = true
    error = null
    try {
      pack = await api.get(projectId, packId)
      scenario = pack.scenarios.find(s => s.id === scenarioId)
      if (!scenario) { error = 'Scenario not found'; loading = false; m.redraw(); return }

      const scenarioDetails = (pack.scenarioDetails || []).filter(sd => sd.scenario_id === scenarioId)
      const relevantIds = new Set(scenarioDetails.map(sd => sd.detail_id))
      relevantDetails = (pack.details || []).filter(d => relevantIds.has(d.id))

      try { irrelevantDetails = await api.getIrrelevantDetails(projectId, packId, scenarioId) } catch { irrelevantDetails = [] }
      try { noiseSnippets = await api.getNoiseSnippets(projectId, packId, scenarioId) } catch { noiseSnippets = [] }
      try { stylePresets = await stylePresetsApi.list(projectId) } catch { stylePresets = [] }

      if (isRemix && generationId) {
        const generations = await api.getGenerations(projectId, packId, scenarioId)
        remixGeneration = generations.find(g => g.id === generationId)
        if (!remixGeneration) { error = 'Generation not found'; loading = false; m.redraw(); return }

        content = remixGeneration.content
        let snapshot = {}
        try { snapshot = JSON.parse(remixGeneration.config_snapshot) } catch {}
        // Separate relevant detail values from irrelevant ones
        const allDv = snapshot.detailValues || {}
        const irrSet = new Set(snapshot.irrelevantDetailsUsed || [])
        detailValues = {}
        noiseDetailValues = {}
        for (const [k, v] of Object.entries(allDv)) {
          if (irrSet.has(k)) noiseDetailValues[k] = v
          else detailValues[k] = v
        }
        selectedStyleId = snapshot.stylePresetId || null
        activeNoiseDetails = irrSet
        activeSnippets = new Set(snapshot.noiseSnippetsUsed || [])
      } else {
        // Full shuffle on first load
        shuffle()
      }
    } catch (e) {
      error = e.message
    } finally {
      loading = false
      m.redraw()
    }
  }

  function renderDetailChips() {
    if (relevantDetails.length === 0) return null
    const children = []
    children.push(m('div', { key: 'details-label', class: sectionLabel }, 'Details'))
    const chips = []
    for (const d of relevantDetails) {
      const val = detailValues[d.id]
      const label = val ? `${d.name}: ${getValueLabel(d, val)}` : d.name
      const isOpen = openDropdown === d.id
      const isUnknown = val === '_unknown'

      chips.push(m('div', {
        key: d.id,
        style: 'position: relative; display: inline-flex;',
      }, [
        m('div', {
          class: isUnknown ? chip : detailChipActive,
          onclick: (e) => {
            e.stopPropagation()
            openDropdown = isOpen ? null : d.id
          },
        }, [label, ' \u25BE']),
        isOpen ? m('div', { class: dropdown, onclick: (e) => e.stopPropagation() },
          getDetailOptions(d).map(opt =>
            m('div', {
              key: opt.id,
              class: opt.id === val ? dropdownItemActive : dropdownItem,
              onclick: () => {
                detailValues = { ...detailValues, [d.id]: opt.id }
                openDropdown = null
              },
            }, opt.label)
          )
        ) : null,
      ]))
    }
    children.push(m('div', { key: 'details-chips', class: chipWrap }, chips))
    return children
  }

  function renderStyleChips() {
    if (stylePresets.length === 0) return null

    const preset = selectedStyleId ? stylePresets.find(p => p.id === selectedStyleId) : null
    const isClickable = stylePresets.length > 1
    const children = []

    // Section label with counter
    const currentIdx = preset ? stylePresets.findIndex(p => p.id === selectedStyleId) : -1
    const counterText = isClickable ? ` (${currentIdx + 1} of ${stylePresets.length})` : ''
    children.push(m('div', { key: 'style-label', class: sectionLabel }, 'Style' + counterText))

    if (!preset) {
      // No preset selected but presets exist — show prompt to pick one
      children.push(m('div', {
        key: 'style-card',
        class: styleDisplayClickable,
        onclick: () => { cycleStyle(); m.redraw() },
        title: 'Click to choose a style',
      }, m('div', { style: `color: ${colors.textMuted}; font-size: ${font.size.sm};` }, 'Click to choose a style')))
      return children
    }

    const tones = preset.tone ? preset.tone.split(',').map(t => t.trim()).filter(Boolean) : []
    const formLabel = preset.form ? preset.form.replace(/_/g, ' ') : null
    const cardItems = []
    cardItems.push(m('div', { key: 'label', style: `font-weight: ${font.weight.semibold}; margin-bottom: 2px;` }, preset.label))
    if (formLabel) cardItems.push(m('div', { key: 'form', style: `font-size: ${font.size.sm}; color: ${colors.textSecondary};` }, formLabel))
    if (tones.length > 0) cardItems.push(m('div', { key: 'tones', style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; margin-top: 2px;` }, tones.join(' \u00B7 ')))
    if (preset.custom_prompt) cardItems.push(m('div', { key: 'custom', style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; margin-top: 4px; font-style: italic; white-space: pre-wrap;` }, preset.custom_prompt))
    children.push(m('div', {
      key: 'style-card',
      class: isClickable ? styleDisplayClickable : styleDisplay,
      onclick: isClickable ? () => { cycleStyle(); m.redraw() } : undefined,
      title: isClickable ? 'Click to cycle to next style' : undefined,
    }, cardItems))

    let parsedExamples = []
    if (preset.examples) {
      try { parsedExamples = JSON.parse(preset.examples) } catch { /* ignore */ }
    }
    if (parsedExamples.length > 0) {
      children.push(m('div', {
        key: 'examples-toggle',
        style: `font-size: ${font.size.sm}; color: ${colors.brand}; cursor: pointer; margin-bottom: ${space.sm}; user-select: none;`,
        onclick: (e) => { e.stopPropagation(); showExamples = !showExamples },
      }, `${showExamples ? '\u25BE' : '\u25B8'} ${parsedExamples.length} example message${parsedExamples.length !== 1 ? 's' : ''}`))

      if (showExamples) {
        for (let i = 0; i < parsedExamples.length; i++) {
          children.push(m('div', {
            key: `example-${i}`,
            style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; padding: ${space.sm} ${space.md}; background: ${colors.bgSidebar}; border: 1px solid ${colors.border}; border-radius: 6px; margin-bottom: ${space.xs}; white-space: pre-wrap;`,
          }, parsedExamples[i]))
        }
      }
    }

    return children
  }

  function renderNoiseChips() {
    const noiseDetails = (irrelevantDetails || []).map(irr => {
      const did = irr.detail_id || irr
      return (pack.details || []).find(dd => dd.id === did)
    }).filter(Boolean)
    const hasNoise = noiseDetails.length > 0 || (noiseSnippets || []).length > 0
    if (!hasNoise) return null

    const children = []
    children.push(m('div', { key: 'noise-label', class: sectionLabel }, 'Noise'))

    // Irrelevant details as picker chips (like relevant details)
    const noiseChips = []
    for (const d of noiseDetails) {
      const isActive = activeNoiseDetails.has(d.id)
      const val = noiseDetailValues[d.id]
      const label = isActive && val
        ? `${d.name}: ${getValueLabel(d, val)}`
        : d.name
      const isOpen = openDropdown === `noise-${d.id}`

      noiseChips.push(m('div', {
        key: `noise-${d.id}`,
        style: 'position: relative; display: inline-flex;',
      }, [
        m('div', {
          class: isActive ? detailChipActive : chip,
          onclick: (e) => {
            e.stopPropagation()
            if (!isActive) {
              // Activate with a random value
              const s = new Set(activeNoiseDetails)
              s.add(d.id)
              activeNoiseDetails = s
              const vals = shuffleDetailValues([d])
              if (vals[d.id]) noiseDetailValues = { ...noiseDetailValues, [d.id]: vals[d.id] }
            } else {
              // Open dropdown to change value, or deactivate with second click on label
              openDropdown = isOpen ? null : `noise-${d.id}`
            }
          },
        }, [label, isActive ? ' \u25BE' : null]),
        isOpen ? m('div', { class: dropdown, onclick: (e) => e.stopPropagation() }, [
          ...getDetailOptions(d).filter(o => o.id !== '_unknown').map(opt =>
            m('div', {
              key: opt.id,
              class: opt.id === val ? dropdownItemActive : dropdownItem,
              onclick: () => {
                noiseDetailValues = { ...noiseDetailValues, [d.id]: opt.id }
                openDropdown = null
              },
            }, opt.label)
          ),
          m('div', {
            key: '_remove',
            class: dropdownItem,
            style: `color: ${colors.textMuted}; border-top: 1px solid ${colors.border}; margin-top: 2px; padding-top: 6px;`,
            onclick: () => {
              const s = new Set(activeNoiseDetails)
              s.delete(d.id)
              activeNoiseDetails = s
              openDropdown = null
            },
          }, 'Remove'),
        ]) : null,
      ]))
    }

    // Snippet chips (simple toggles)
    for (const s of (noiseSnippets || [])) {
      const isActive = activeSnippets.has(s.id)
      noiseChips.push(m('div', {
        key: `snippet-${s.id}`,
        class: isActive ? chipActive : chip,
        onclick: () => {
          const set = new Set(activeSnippets)
          isActive ? set.delete(s.id) : set.add(s.id)
          activeSnippets = set
        },
      }, s.content))
    }

    children.push(m('div', { key: 'noise-chips', class: chipWrap }, noiseChips))
    return children
  }

  function closeDropdowns(e) {
    if (openDropdown) {
      openDropdown = null
      m.redraw()
    }
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      packId = vnode.attrs.packId
      scenarioId = vnode.attrs.scenarioId
      generationId = vnode.attrs.generationId
      isRemix = !!generationId
      load()
      document.addEventListener('click', closeDropdowns)
    },
    onremove() {
      document.removeEventListener('click', closeDropdowns)
    },
    view() {
      if (loading) return m('div', { class: emptyState }, 'Loading...')
      if (error && !pack) return m('div', { class: emptyState }, error)

      const packLabel = pack ? pack.label : ''
      const scenarioContent = scenario ? scenario.content : ''
      const allChildren = []

      // Breadcrumb
      allChildren.push(m('div', { key: 'bc', class: breadcrumb }, [
        m(m.route.Link, { href: '/projects' }, 'Projects'),
        ' / ',
        m(m.route.Link, { href: `/projects/${projectId}` }, 'Project'),
        ' / ',
        m(m.route.Link, { href: `/projects/${projectId}/scenarios` }, 'Scenarios'),
        ' / ',
        m(m.route.Link, { href: `/projects/${projectId}/scenarios/${packId}` }, packLabel),
        ' / ',
        m(m.route.Link, { href: `/projects/${projectId}/scenarios/${packId}/${scenarioId}/generate` }, 'Generate'),
        ' / ',
        isRemix ? 'Remix' : 'Write',
      ]))

      // Title
      allChildren.push(m('h1', { key: 'title', class: pageTitle },
        isRemix ? 'Remix Scenario' : 'Write Scenario'
      ))

      // Error
      if (error) {
        allChildren.push(m('div', {
          key: 'error',
          style: `color: ${colors.critical}; margin-bottom: ${space.md};`,
        }, error))
      }

      // Scenario context
      if (scenarioContent && scenarioContent !== '(new scenario)') {
        allChildren.push(m('div', { key: 'ctx', class: scenarioContext }, scenarioContent))
      }

      // Main card
      const cardChildren = []

      // Detail chips
      const detailSection = renderDetailChips()
      if (detailSection) cardChildren.push(...detailSection)

      // Style chips
      const styleSection = renderStyleChips()
      if (styleSection) cardChildren.push(...styleSection)

      // Noise chips
      const noiseSection = renderNoiseChips()
      if (noiseSection) cardChildren.push(...noiseSection)

      // Textarea
      cardChildren.push(m('textarea', {
        key: 'textarea',
        class: textArea,
        placeholder: 'Write your scenario here...',
        value: content,
        oninput: (e) => { content = e.target.value },
      }))

      // Buttons
      if (isRemix) {
        cardChildren.push(m('div', { key: 'buttons', class: buttonBar }, [
          m('button', {
            key: 'cancel',
            class: bigBtnSecondary,
            onclick: navigateBack,
          }, 'Cancel'),
          m('button', {
            key: 'save-new',
            class: bigBtnSecondary,
            disabled: saving || !content.trim(),
            onclick: saveAsNew,
          }, 'Save as New'),
          m('button', {
            key: 'save',
            class: bigBtnPrimary,
            disabled: saving || !content.trim(),
            onclick: saveRemix,
          }, saving ? 'Saving...' : 'Save'),
        ]))
      } else {
        cardChildren.push(m('div', { key: 'buttons', class: buttonBar }, [
          m('button', {
            key: 'shuffle',
            class: bigBtnSecondary,
            onclick: () => { shuffle(); m.redraw() },
          }, 'Shuffle'),
          m('button', {
            key: 'save-shuffle',
            class: bigBtnPrimary,
            disabled: saving || !content.trim(),
            onclick: saveAndShuffle,
          }, saving ? 'Saving...' : 'Save & Shuffle'),
          m('button', {
            key: 'save-repeat',
            class: bigBtnPrimary,
            disabled: saving || !content.trim(),
            onclick: saveAndRepeat,
          }, saving ? 'Saving...' : 'Save & Repeat'),
        ]))

        // Saved toast & counter
        if (showSavedToast) {
          cardChildren.push(m('div', { key: 'toast', class: savedToast }, 'Saved!'))
        }
        if (savedCount > 0) {
          cardChildren.push(m('div', { key: 'counter', class: counterBadge }, `${savedCount} saved this session`))
        }
      }

      allChildren.push(m('div', { key: 'card', class: writerCard }, cardChildren))

      return m('div', allChildren)
    },
  }
}
